import React, { useEffect, useId, useRef, useState, type JSX } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Input, Select, ToggleButtonGroup } from '@/components/atoms';
import {
  cmsColSpanClass,
  useCmsFormSaving,
} from '@/components/organisms/CmsForm';
import type { CmsColSpan } from '@/components/organisms/CmsForm/cmsColSpan';
import { CmsDateField, CmsFieldShell, CmsSelectField } from '@/components/molecules/CmsField';
import type { RecurrenceRule, RecurrenceUnit } from '@/types';
import {
  buildDefaultRecurrenceRule,
  ensureByWeekDay,
  formatRecurrenceSummary,
  generateOccurrences,
  getWeekdayFromStartsAt,
} from '@/utils';
import './CmsRecurrenceField.scss';

const UNIT_OPTIONS: Record<string, string> = {
  DAY: 'Días',
  WEEK: 'Semanas',
};

const WEEKDAY_SHORT: Record<string, string> = {
  '0': 'D', '1': 'L', '2': 'M', '3': 'X', '4': 'J', '5': 'V', '6': 'S',
};

const WEEKDAY_LONG: Record<string, string> = {
  '0': 'Domingos', '1': 'Lunes', '2': 'Martes', '3': 'Miércoles', '4': 'Jueves', '5': 'Viernes', '6': 'Sábados',
};

const WEEKDAY_OPTIONS: Record<string, JSX.Element> = Object.fromEntries(
  Object.keys(WEEKDAY_SHORT).map((k) => [
    k,
    <>
      <span className="weekday-short">{WEEKDAY_SHORT[k]}</span>
      <span className="weekday-long">{WEEKDAY_LONG[k]}</span>
    </>,
  ]),
);

const RECURRENCE_MODE_OPTIONS: Record<string, string> = {
  NONE: 'No se repite',
  RECURRING: 'Evento recurrente',
};

interface CmsRecurrenceFieldProps {
  /** ISO datetime of the first occurrence — drives default weekday and summary times */
  startsAt: string;
  /** ISO datetime of the first occurrence end — used for summary time range */
  endsAt: string;
  /** Null means one-time event; a RecurrenceRule means recurring */
  value: RecurrenceRule | null;
  onChange: (value: RecurrenceRule | null) => void;
  untilError?: string;
  weekDaysError?: string;
  className?: string;
  /** Grid span for the Repetición select (same as other CMS fields). Rule details always span full width. */
  colSpan?: CmsColSpan;
  disabled?: boolean;
}

/**
 * CMS form field for configuring event recurrence.
 * Shows a NONE / RECURRING select and expands the rule fields when RECURRING is selected.
 * Expects to be rendered inside `.cms-form-page__fields` (wrapper uses `display: contents`).
 */
export function CmsRecurrenceField({
  startsAt,
  endsAt,
  value,
  onChange,
  untilError,
  weekDaysError,
  className,
  colSpan,
  disabled,
}: CmsRecurrenceFieldProps) {
  const isFormSaving = useCmsFormSaving();
  const isDisabled = disabled || isFormSaving;
  const intervalLabelId = useId();
  const weekdayFieldId = useId();
  const weekdayLabelId = useId();
  const weekdayErrorId = weekDaysError ? `${weekdayFieldId}-error` : undefined;
  const isRecurring = value !== null;
  const defaultRule = buildDefaultRecurrenceRule(startsAt);

  const draftRef = useRef<RecurrenceRule>(value ?? defaultRule);

  const prevStartsAtRef = useRef(startsAt);
  useEffect(() => {
    if (prevStartsAtRef.current !== startsAt) {
      prevStartsAtRef.current = startsAt;
      if (!isRecurring) {
        draftRef.current = {
          ...draftRef.current,
          byWeekDay: [getWeekdayFromStartsAt(startsAt)],
        };
      }
    }
  }, [startsAt, isRecurring]);

  const [localInterval, setLocalInterval] = useState(String(value?.interval ?? defaultRule.interval));
  const [localUnit, setLocalUnit] = useState<RecurrenceUnit>(value?.unit ?? defaultRule.unit);
  const [localByWeekDay, setLocalByWeekDay] = useState<string[]>(
    ensureByWeekDay(value?.byWeekDay, startsAt).map(String),
  );
  const [localUntil, setLocalUntil] = useState(value?.until ?? '');

  useEffect(() => {
    if (value) {
      setLocalInterval(String(value.interval));
      setLocalUnit(value.unit);
      if (value.unit === 'WEEK') {
        setLocalByWeekDay(ensureByWeekDay(value.byWeekDay, startsAt).map(String));
      }
      setLocalUntil(value.until);
    }
  }, [value, startsAt]);

  const buildRule = (
    interval: string,
    unit: RecurrenceUnit,
    byWeekDay: string[],
    until: string,
  ): RecurrenceRule => ({
    interval: Math.max(1, Number(interval) || 1),
    unit,
    byWeekDay:
      unit === 'WEEK'
        ? ensureByWeekDay(byWeekDay.map(Number), startsAt)
        : undefined,
    until,
  });

  const handleModeChange = (mode: string) => {
    if (mode === 'NONE') {
      draftRef.current = buildRule(localInterval, localUnit, localByWeekDay, localUntil);
      onChange(null);
    } else {
      const draft = buildRule(
        String(draftRef.current.interval),
        draftRef.current.unit,
        (draftRef.current.byWeekDay ?? []).map(String),
        draftRef.current.until,
      );
      draftRef.current = draft;
      setLocalInterval(String(draft.interval));
      setLocalUnit(draft.unit);
      setLocalByWeekDay((draft.byWeekDay ?? []).map(String));
      setLocalUntil(draft.until);
      onChange(draft);
    }
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLocalInterval(raw);
    onChange(buildRule(raw, localUnit, localByWeekDay, localUntil));
  };

  const handleUnitChange = (unit: string) => {
    const typedUnit = unit as RecurrenceUnit;
    const normalizedDays =
      typedUnit === 'WEEK'
        ? ensureByWeekDay(localByWeekDay.map(Number), startsAt).map(String)
        : localByWeekDay;

    setLocalUnit(typedUnit);
    if (typedUnit === 'WEEK') {
      setLocalByWeekDay(normalizedDays);
    }
    onChange(buildRule(localInterval, typedUnit, normalizedDays, localUntil));
  };

  const handleWeekDayChange = (days: string[]) => {
    const baseDay = String(getWeekdayFromStartsAt(startsAt));
    const normalized = days.length === 0 ? [baseDay] : days;
    setLocalByWeekDay(normalized);
    onChange(buildRule(localInterval, localUnit, normalized, localUntil));
  };

  const handleUntilChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalUntil(e.target.value);
    onChange(buildRule(localInterval, localUnit, localByWeekDay, e.target.value));
  };

  const summary =
    isRecurring && value && value.until && startsAt && endsAt
      ? formatRecurrenceSummary(value, startsAt, endsAt)
      : null;

  const occurrenceCount =
    isRecurring && value && value.until && startsAt
      ? generateOccurrences(startsAt, value).length
      : 0;

  return (
    <div className={['cms-recurrence-field', className].filter(Boolean).join(' ')}>
      <CmsSelectField
        label="Repetición"
        hint="Para eventos reiterativos."
        options={RECURRENCE_MODE_OPTIONS}
        value={isRecurring ? 'RECURRING' : 'NONE'}
        onChange={handleModeChange}
        disabled={isDisabled}
        variant="primary"
        motionScale="lg"
        colSpan={colSpan}
      />

      <Collapsible.Root
        open={isRecurring}
        className={[
          'cms-recurrence-field__collapsible',
          cmsColSpanClass(6),
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Collapsible.Content className="cms-recurrence-field__details">
          <div className="cms-recurrence-field__details-panel">
          <CmsFieldShell
            label="Intervalo de repetición"
            labelId={intervalLabelId}
            hint="Se repite cada N días o semanas"
            className="cms-recurrence-field__interval"
          >
            <div className="cms-recurrence-field__interval-inputs">
              <Input
                type="number"
                numberSteppers
                min={1}
                max={52}
                value={localInterval}
                onChange={handleIntervalChange}
                density="default"
                disabled={isDisabled}
                className="cms-recurrence-field__interval-number"
                aria-labelledby={intervalLabelId}
              />
              <Select
                options={UNIT_OPTIONS}
                value={localUnit}
                onChange={handleUnitChange}
                variant="primary"
                density="default"
                motionScale="lg"
                disabled={isDisabled}
                fullWidth={false}
                aria-labelledby={intervalLabelId}
                className="cms-recurrence-field__unit-toggle"
              />
            </div>
          </CmsFieldShell>

          {localUnit === 'WEEK' && (
            <CmsFieldShell
              label="Días de la semana"
              labelId={weekdayLabelId}
              id={weekdayFieldId}
              hint="Se repite el evento en estos días"
              error={weekDaysError}
              className={[
                'cms-recurrence-field__weekday',
                weekDaysError && 'cms-field--fieldset-error',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <ToggleButtonGroup
                options={WEEKDAY_OPTIONS}
                value={localByWeekDay}
                onChange={handleWeekDayChange}
                density="compact"
                disabled={isDisabled}
                aria-labelledby={weekdayLabelId}
                aria-describedby={weekdayErrorId}
                className="cms-field__input cms-recurrence-field__weekdays"
              />
            </CmsFieldShell>
          )}

          <CmsDateField
            label="Fin de la repetición"
            hint="Hasta cuando se repite el intervalo"
            id="recurrence-until"
            type="date"
            value={localUntil}
            onChange={handleUntilChange}
            error={untilError}
            requiredMark
            className="cms-recurrence-field__until"
          />

          {summary && (
            <p className="cms-recurrence-field__summary">
              {summary}
              {occurrenceCount > 0 && (
                <span className="cms-recurrence-field__count">
                  {' '}— se crearán {occurrenceCount} evento{occurrenceCount !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          )}
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}
